import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ExternalLink, Copy, Check } from 'lucide-react'
import { toast } from 'sonner'

interface TelegramModalProps {
  isOpen: boolean
  onClose: () => void
  onConnect: (token: string) => void
}

export function TelegramModal({
  isOpen,
  onClose,
  onConnect,
}: TelegramModalProps) {
  const [token, setToken] = useState('')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText('/newbot')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success('Command copied to clipboard')
  }

  const handleConnect = () => {
    if (!token.trim()) {
      toast.error('Please enter a valid bot token')
      return
    }
    onConnect(token)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-card border-border text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 ">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png"
              className="w-6 h-6"
              alt="Telegram"
            />
            Connect Telegram Bot
          </DialogTitle>
          <DialogDescription className="text-muted-foreground/90">
            Follow these steps to create a bot and get your token.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Step 1: Create a new bot
            </h4>
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md border border-border/50">
              <p className="mb-2">
                1. Open{' '}
                <a
                  href="https://t.me/BotFather"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  @BotFather <ExternalLink className="w-3 h-3" />
                </a>{' '}
                in Telegram.
              </p>
              <p className="mb-2 flex items-center gap-2">
                2. Send the command
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-background border border-border text-xs font-mono text-foreground hover:border-primary/50 transition-colors"
                >
                  /newbot
                  {copied ? (
                    <Check className="w-3 h-3 text-success" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </p>
              <p>3. Follow the instructions to name your bot.</p>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Step 2: Enter your token
            </h4>
            <div className="relative">
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="123456789:ABCdefGhIjklmNoPqRsTuVwXyZ"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                name="token"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              You'll receive this token from BotFather after creating your bot.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-border hover:bg-muted text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Connect Bot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
